import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, ArrowRight, User, Building, CheckCircle, Check, X, Sparkles } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Signup = () => {
  const { register, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [userType, setUserType] = useState<'renter' | 'landlord'>('renter');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    referralCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  const validatePassword = (password: string) => {
    const requirements = {
      minLength: password.length >= 10,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    setPasswordRequirements(requirements);
    return Object.values(requirements).every(req => req);
  };

  const isPasswordValid = Object.values(passwordRequirements).every(req => req);

  useEffect(() => {
    const type = searchParams.get('type');
    if (type === 'landlord') {
      setUserType('landlord');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password mismatch",
        description: "Please make sure your passwords match.",
        variant: "destructive"
      });
      return;
    }

    if (!isPasswordValid) {
      toast({
        title: "Password requirements not met",
        description: "Please ensure your password meets all requirements.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.fullName.trim()) {
      toast({
        title: "Name required",
        description: "Please enter your full name.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await register(formData.email, formData.password, {
        full_name: formData.fullName.trim(),
        user_type: userType,
        referred_by: formData.referralCode.trim() || undefined,
        payment_status: 'none',
        payment_required: false,
        emailRedirectTo: `${window.location.origin}/auth/confirm`
      });

      if (result.error) {
        toast({
          title: "Sign up failed",
          description: result.error.message || "Please try again.",
          variant: "destructive"
        });
        return;
      }

      setEmailSent(true);
    } catch (error: any) {
      console.error('Signup error:', error);
      toast({
        title: "Sign up failed",
        description: error.message || "Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Email confirmation screen
  if (emailSent) {
    return (
      <div className="min-h-screen relative flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: `url('/lovable-uploads/ce465d72-0955-4d65-855f-08ec6537d674.png')`
        }} />
        <div className="absolute inset-0 bg-black/60" />
        
        <div className="relative z-10 w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-8">
              <img src="/lovable-uploads/d981c4c2-888d-4322-9d83-e527475f379f.png" alt="Flamingo Logo" className="h-8 w-auto mx-auto" />
            </Link>
            
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Check Your Email
              </h1>
              <p className="text-white/80">
                We've sent a confirmation link to <span className="font-medium text-white">{formData.email}</span>
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 space-y-4 shadow-2xl">
            <p className="text-sm text-gray-900">
              Please check your email and click the confirmation link to activate your account.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Tip:</strong> Don't forget to check your spam folder if you don't see the email within a few minutes.
              </p>
            </div>

            <div className="pt-2">
              <Button 
                variant="outline" 
                onClick={() => setEmailSent(false)} 
                className="w-full"
              >
                Try a different email address
              </Button>
            </div>
          </div>

          <div className="text-center mt-6">
            <Link to="/login" className="text-sm text-white/80 hover:text-white transition-colors">
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: `url('/lovable-uploads/ce465d72-0955-4d65-855f-08ec6537d674.png')`
      }} />
      <div className="absolute inset-0 bg-black/50" />
      
      <div className="relative z-10 w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-8">
            <img src="/lovable-uploads/d981c4c2-888d-4322-9d83-e527475f379f.png" alt="Flamingo Logo" className="h-8 w-auto mx-auto" />
          </Link>
          
          <h1 className="text-3xl font-bold text-white mb-2">
            Join Flamingo
          </h1>
          <p className="text-white/80 text-lg">
            {userType === 'renter' 
              ? 'Start your rental journey - completely free!' 
              : 'Start managing properties for free'
            }
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          {/* User Type Selection */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 text-center">I am a...</h2>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setUserType('renter')}
                className={`flex flex-col items-center justify-center p-5 border-2 rounded-xl transition-all font-medium ${
                  userType === 'renter'
                    ? 'border-primary bg-primary text-white shadow-lg'
                    : 'border-gray-200 hover:border-primary/50 hover:bg-primary/5 text-gray-900'
                }`}
              >
                <User className="w-8 h-8 mb-2" />
                <span className="text-lg font-semibold">Renter</span>
                <span className={`text-xs mt-1 ${userType === 'renter' ? 'text-white/80' : 'text-gray-500'}`}>
                  Find your next home
                </span>
              </button>
              <button
                type="button"
                onClick={() => setUserType('landlord')}
                className={`flex flex-col items-center justify-center p-5 border-2 rounded-xl transition-all font-medium ${
                  userType === 'landlord'
                    ? 'border-primary bg-primary text-white shadow-lg'
                    : 'border-gray-200 hover:border-primary/50 hover:bg-primary/5 text-gray-900'
                }`}
              >
                <Building className="w-8 h-8 mb-2" />
                <span className="text-lg font-semibold">Landlord</span>
                <span className={`text-xs mt-1 ${userType === 'landlord' ? 'text-white/80' : 'text-gray-500'}`}>
                  List properties
                </span>
              </button>
            </div>

            {/* Value proposition based on user type */}
            <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
              {userType === 'renter' ? (
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">100% Free for Renters</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Unlimited swipes, AI matching, messaging & more - all features included free
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Free to Start</p>
                    <p className="text-xs text-gray-600 mt-1">
                      No upfront costs. Pay per unit as your portfolio grows: $12/unit for 1-3 units, less for more
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Account Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Your Account</h3>
              
              {/* Full Name */}
              <div className="mb-4">
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-900 mb-2">
                  Full Name *
                </label>
                <Input
                  id="fullName"
                  type="text"
                  value={formData.fullName}
                  onChange={e => updateFormData('fullName', e.target.value)}
                  required
                  placeholder="Enter your full name"
                  className="h-12"
                />
              </div>

              {/* Email */}
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                  Email Address *
                </label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={e => updateFormData('email', e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="h-12"
                />
              </div>

              {/* Password */}
              <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-gray-900 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={e => {
                      updateFormData('password', e.target.value);
                      validatePassword(e.target.value);
                    }}
                    required
                    placeholder="Create a strong password"
                    className="h-12 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password Requirements */}
                {formData.password && (
                  <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-700 mb-2">Password requirements:</p>
                    <div className="grid grid-cols-2 gap-1">
                      {[
                        { key: 'minLength', label: '10+ characters' },
                        { key: 'hasUppercase', label: 'Uppercase letter' },
                        { key: 'hasLowercase', label: 'Lowercase letter' },
                        { key: 'hasNumber', label: 'Number' },
                        { key: 'hasSpecialChar', label: 'Special character' }
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center gap-1.5">
                          {passwordRequirements[key as keyof typeof passwordRequirements] ? (
                            <Check className="w-3 h-3 text-green-500" />
                          ) : (
                            <X className="w-3 h-3 text-gray-400" />
                          )}
                          <span className={`text-xs ${passwordRequirements[key as keyof typeof passwordRequirements] ? 'text-green-700' : 'text-gray-500'}`}>
                            {label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="mb-4">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900 mb-2">
                  Confirm Password *
                </label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={e => updateFormData('confirmPassword', e.target.value)}
                    required
                    placeholder="Confirm your password"
                    className="h-12 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>

              {/* Referral Code (Optional) */}
              <div className="mb-6">
                <label htmlFor="referralCode" className="block text-sm font-medium text-gray-900 mb-2">
                  Referral Code <span className="text-gray-400">(Optional)</span>
                </label>
                <Input
                  id="referralCode"
                  type="text"
                  value={formData.referralCode}
                  onChange={e => updateFormData('referralCode', e.target.value)}
                  placeholder="Enter referral code"
                  className="h-12"
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              disabled={isSubmitting || !isPasswordValid}
              className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-500/90"
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating account...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  Get Started Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </span>
              )}
            </Button>

            {/* Terms */}
            <p className="text-xs text-center text-gray-500 mt-4">
              By signing up, you agree to our{' '}
              <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
            </p>
          </form>
        </div>

        {/* Sign In Link */}
        <div className="text-center mt-6">
          <span className="text-white/80">Already have an account?</span>{' '}
          <Link to="/login" className="text-white font-medium hover:underline">
            Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
